import React, { useState, useRef } from "react";
import Banner from "../components/Banner";
import page1 from "./page1.png";
import earthImage from "./earthImage.png";

function Home() {
  const [showSurvey, setShowSurvey] = useState(true);
  const [showDoc, setShowDoc] = useState(false);
  const initialFormData = {
    email: "",
    priorityOne: "",
    priorityFour: [],
    challenges: "",
    opportunities: "",
    name: "",
    district: "",
    ageGroup: "",
    gender: "",
    whatsapp: "",
    movement: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});

  const refs = {
    email: useRef(null),
    priorityOne: useRef(null),
    priorityFour: useRef(null),
    challenges: useRef(null),
    opportunities: useRef(null),
    name: useRef(null),
    district: useRef(null),
    ageGroup: useRef(null),
    gender: useRef(null),
    whatsapp: useRef(null),
    movement: useRef(null),
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // ✅ clear error for this field
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const copy = { ...prev };
      delete copy[name];
      return copy;
    });
  };

  const handleCheckbox = (field, value) => {
    setFormData((prev) => {
      const list = prev[field] || [];
      const updated = list.includes(value)
        ? list.filter((v) => v !== value)
        : [...list, value];

      return { ...prev, [field]: updated };
    });

    // ✅ clear error once at least one is selected
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
  };

  const validate = () => {
    const e = {};
    if (!formData.email) e.email = true;
    if (!formData.priorityOne) e.priorityOne = true;
    if (!formData.priorityFour.length) e.priorityFour = true;
    if (!formData.challenges) e.challenges = true;
    if (!formData.opportunities) e.opportunities = true;
    if (!formData.name) e.name = true;
    if (!formData.district.length) e.district = true;
    if (!formData.ageGroup.length) e.ageGroup = true;
    if (!formData.gender) e.gender = true;
    if (!formData.whatsapp) e.whatsapp = true;
    if (!formData.movement) e.movement = true;

    setErrors(e);

    if (Object.keys(e).length) {
      refs[Object.keys(e)[0]].current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      email: formData.email,
      top_priority: formData.priorityOne,
      other_priorities: formData.priorityFour,
      challenge_faced: formData.challenges,
      greatest_opportunity: formData.opportunities,
      name: formData.name,
      district: formData.district,
      age_group: formData.ageGroup,
      gender: formData.gender,
      whatsapp_number: formData.whatsapp,
      join_movement: formData.movement,
    };

    try {
      const response = await fetch(
        "https://api-discloseai.essolutions.ai/api/v1/documents/take-survey",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error("Failed to submit complaint");

      setShowSurvey(false);
      resetSurveyForm();
    } catch (err) {
      console.error("Submission error:", err);
    } finally {
      setShowSurvey(false);
    }

    console.log("SUBMIT DATA →", payload);
  };

  const resetSurveyForm = () => {
    setFormData(initialFormData);
    setErrors({});
  };

  const handleCopy = async () => {
    const urlToCopy = "https://www.seas2050.org/surveylink"; // 👈 replace with your URL

    try {
      await navigator.clipboard.writeText(urlToCopy);
      alert("Link copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  return (
    <>
      <Banner
        title="SEA'S 2050"
        subtitle="SEA’s 2050 evaluates development choices before they are made helping decision-makers balance growth, equity, and environmental protection."
      />

      <div className="mt-4 text-center">
        <h5 style={{ color: "#1155cc", marginBottom: "6px" }}>
          Updated Draft Scoping Report (Aug 2026)
        </h5>

        <p style={{ color: "#94a3b8", fontSize: "14px", marginBottom: "14px" }}>
          Review the preliminary findings, insights, and strategic direction.
        </p>

        <div style={{ display: "flex", justifyContent: "center" }}>
          <button
            className="btn btn-outline-primary btn-lg px-4"
            onClick={() => setShowDoc(true)}
          >
            View Document
          </button>
        </div>
      </div>

      {/* CONTENT SECTION */}
      <section className="py-5" id="Future">
        <div className="container text-center">
          {/* Heading */}
          <h2 className="fw-bold mb-4" style={{ color: "#1155cc" }}>
            Shaping Sierra Leone’s Sustainable Future Together
          </h2>

          {/* Paragraphs */}
          <div className="row justify-content-center">
            <div className="col-lg-10 col-xl-9">
              <p className="fs-5 text-muted mb-3">
                SEA’s 2050 is a national, citizen-driven initiative designed to
                develop a long-term, sustainable development framework for
                Sierra Leone through to the year 2050. It represents the first
                global effort led by Sierra Leoneans at home and abroad to
                collectively define the country’s economic, social, and
                environmental future
              </p>

              <p className="fs-5 text-muted mb-3">
                Using the globally recognized Strategic Environmental Assessment
                (SEA) methodology, this initiative ensures that sustainability,
                inclusivity, and resilience are embedded in national planning
                and decision-making processes.
              </p>

              <p className="fs-5 text-muted mb-3">
                What makes SEA’s 2050 exceptional is its scale and innovation.
                The initiative combines broad public participation particularly
                engaging young people under the age of 30 with advanced data
                analytics and artificial intelligence to analyse large volumes
                of public input. This guarantees that every contribution helps
                shape the final national vision
              </p>

              <p className="fs-5 text-muted mb-4">
                SEA’s 2050 aims to produce a practical, evidence-based blueprint
                that will guide government policies, inform international
                partners, and support long-term national development planning.
              </p>
            </div>
          </div>

          <h5
            className="fw-bold fst-italic mt-4 mb-5"
            style={{ color: "#1155cc" }}
          >
            Your voice matters. Join us in shaping Sierra Leone’s future.
          </h5>

          {/* Buttons */}
          <div className="d-flex justify-content-center gap-3 flex-wrap">
            <button
              className="btn btn-primary btn-lg px-4"
              onClick={() => setShowSurvey(true)}
            >
              take the Survey
            </button>

            <button
              className="btn btn-outline-primary btn-lg px-4"
              onClick={() => (window.location.href = "/contact")}
            >
              Get Involved
            </button>
          </div>
        </div>
      </section>

      {showDoc && (
        <>
          <div className="modal fade show d-block">
            <div className="modal-dialog modal-xl modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Document Viewer</h5>
                  <button
                    className="btn-close"
                    onClick={() => setShowDoc(false)}
                  />
                </div>

                <div className="modal-body" style={{ height: "70vh" }}>
                  <iframe
                    src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                      "https://seas-project.s3.us-east-1.amazonaws.com/sierra_leone_pilot_sea_for_2050_-_scoping_report__-_july_30_2026.docx"
                    )}`}
                    width="100%"
                    height="100%"
                    style={{ border: "none" }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show" />
        </>
      )}

      {/* SURVEY MODAL */}
      {showSurvey && (
        <>
          <div className="modal fade show d-block survey-modal" tabIndex="-1">
            <div
              className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
              style={{ paddingTop: "63px" }}
            >
              <div className="modal-content rounded-4 shadow-lg">
                {/* HEADER */}
                <div className="modal-header border-0 pb-2">
                  <div>
                    <h4 className="fw-bold mb-1" style={{ color: "#1155cc" }}>
                      SEAS FOR 2050 – A SUSTAINABLE DEVELOPMENT PLAN FOR SIERRA
                      LEONE IN 2050
                    </h4>
                    <p className="fw-bold text-muted mb-0">
                      OUR OPENING QUESTIONNAIRE
                    </p>
                    <button
                      variant="primary"
                      onClick={handleCopy}
                      className="px-4 py-2 fw-semibold"
                      style={{
                        borderRadius: "8px",
                        backgroundColor: "#1155cc",
                        border: "none",
                      }}
                    >
                      Copy Survey Link
                    </button>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      resetSurveyForm();
                      setShowSurvey(false);
                    }}
                  />
                </div>

                {/* BODY */}
                <div className="modal-body pt-3">
                  {/* INTRO */}
                  <div className="intro-box mb-4 fade-slide">
                    <p className="text-muted mb-0">
                      There are no wrong or right answers and you should simply
                      answer with your first thoughts, rather than try and
                      best-guess the outcome. In this opening phase, we simply
                      need to understand what all our stakeholders think are the
                      most important challenges for the future of Salone and
                      their own lives. We also need to know where you are living
                      and a few details about age, gender and education. All
                      information will be held as strictly confidential by our
                      media team which is signed up to international standards
                      of data protection.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit}>
                    {/* EMAIL */}
                    <div ref={refs.email} className="question-row fade-slide">
                      <label className="fw-bold mb-2 d-block">Email</label>
                      <input
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`form-control ${
                          errors.email && "is-invalid"
                        }`}
                      />
                    </div>

                    {/* PRIORITY 1 */}
                    <div className="question-row fade-slide">
                      <p className="fw-bold mb-1">
                        If you can choose only one priority for action from the
                        list below, which one will it be?
                      </p>
                      <p className="text-muted mb-3">
                        My top priority for the next 25 years is:
                      </p>

                      <div ref={refs.priorityOne} className="row g-2">
                        {[
                          "Provide top class education for everyone",
                          "Ensure every household has access to safe drinking water",
                          "Create a reliable electricity supply to all households",
                          "Ensure everyone has enough food so no one goes hungry",
                          "Provide good medical facilities in every community",
                          "Upgrade the roads in my area",
                          "Develop better internet connections and telecom services",
                          "Introduce more job opportunities for the youth",
                        ].map((o, index) => (
                          <div className="col-md-6" key={index}>
                            <label key={o} className="survey-choice">
                              <input
                                type="radio"
                                name="priorityOne"
                                style={{ marginRight: "revert" }}
                                value={o}
                                onChange={handleChange}
                              />
                              {o}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* PRIORITY 4 */}
                    <div
                      ref={refs.priorityFour}
                      className="question-row fade-slide"
                    >
                      <p className="fw-bold mb-3">
                        If you can choose another four priorities for action
                        from the list above what would they be?
                      </p>

                      <div className="row g-2">
                        {[
                          "Provide top class education for everyone",
                          "Ensure every household has access to safe drinking water",
                          "Provide good medical facilities in every community",
                          "Ensure everyone has enough food so no one goes hungry",
                          "Upgrade the roads in my area",
                          "Develop better internet connections and telecom services",
                          "Introduce more job opportunities for the youth",
                          "Create a reliable electricity supply to all households",
                        ].map((o, index) => (
                          <div className="col-md-6" key={index}>
                            <label key={o} className="survey-choice">
                              <input
                                type="checkbox"
                                style={{ marginRight: "revert" }}
                                onChange={() =>
                                  handleCheckbox("priorityFour", o)
                                }
                              />
                              {o}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* CHALLENGES */}
                    <div
                      ref={refs.challenges}
                      className="question-row fade-slide"
                    >
                      <label className="fw-bold mb-2 d-block">
                        What are the most important challenges facing Sierra
                        Leone over the next 25 years?
                      </label>
                      <input
                        name="challenges"
                        value={formData.challenges}
                        onChange={handleChange}
                        className={`form-control ${
                          errors.challenges && "is-invalid"
                        }`}
                      />
                    </div>

                    {/* OPPORTUNITIES */}
                    <div
                      ref={refs.opportunities}
                      className="question-row fade-slide"
                    >
                      <label className="fw-bold mb-2 d-block">
                        What are the greatest opportunities for the Country over
                        the next 25 years?
                      </label>
                      <input
                        name="opportunities"
                        value={formData.opportunities}
                        onChange={handleChange}
                        className={`form-control ${
                          errors.opportunities && "is-invalid"
                        }`}
                      />
                    </div>

                    {/* Name   */}
                    <div ref={refs.name} className="question-row fade-slide">
                      <label className="fw-bold mb-2 d-block">
                        What is your name?
                      </label>
                      <input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`form-control ${
                          errors.name && "is-invalid"
                        }`}
                      />
                    </div>

                    {/* District 5 */}
                    <div
                      ref={refs.district}
                      className="question-row fade-slide"
                    >
                      <p className="fw-bold mb-3">
                        Which District do you live in?
                      </p>

                      <div className="row g-2">
                        {[
                          "Western Area Urban",
                          "Western Area Rural",
                          "Port Loko",
                          "Kambia",
                          "Bombali",
                          "Tonkolili",
                          "Koinadugu",
                          "Falaba",
                          "Bo",
                          "Moyamba",
                          "Bonthe",
                          "Pujehun",
                          "Kenema",
                          "Kailahun",
                          "Kono",
                          "Makeni",
                        ].map((d, index) => (
                          <div className="col-md-6" key={index}>
                            <label key={d} className="survey-choice">
                              <input
                                type="radio"
                                name="district"
                                style={{ marginRight: "revert" }}
                                value={d}
                                onChange={handleChange}
                              />
                              {d}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* age 6 */}
                    <div
                      ref={refs.ageGroup}
                      className="question-row fade-slide"
                    >
                      <p className="fw-bold mb-3">
                        What Age group do you fall into?
                      </p>

                      <div className="row g-2">
                        {[
                          "0-10",
                          "11-20",
                          "21-30",
                          "31-40",
                          "41-50",
                          "51-60",
                          "61-70",
                          "Over 71",
                        ].map((a, index) => (
                          <div className="col-md-6" key={index}>
                            <label key={a} className="survey-choice">
                              <input
                                type="radio"
                                name="ageGroup"
                                style={{ marginRight: "revert" }}
                                value={a}
                                onChange={handleChange}
                              />
                              {a}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Gender 7 */}
                    <div ref={refs.gender} className="question-row fade-slide">
                      <p className="fw-bold mb-3">Gender</p>

                      <div className="row g-2">
                        {["Male", "Female"].map((g, index) => (
                          <div className="col-md-6" key={index}>
                            <label key={g} className="survey-choice">
                              <input
                                type="radio"
                                name="gender"
                                style={{ marginRight: "revert" }}
                                value={g}
                                onChange={handleChange}
                              />
                              {g}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Number 8 */}
                    <div
                      ref={refs.whatsapp}
                      className="question-row fade-slide"
                    >
                      <label className="fw-bold mb-2 d-block">
                        If yes, your WhatsApp Number
                      </label>
                      <input
                        name="whatsapp"
                        value={formData.whatsapp}
                        onChange={handleChange}
                        className={`form-control ${
                          errors.whatsapp && "is-invalid"
                        }`}
                      />
                    </div>

                    {/* movement 8 */}
                    <div
                      ref={refs.movement}
                      className="question-row fade-slide"
                    >
                      <p className="fw-bold mb-3">
                        Will you like to join the movement?
                      </p>

                      <div className="row g-2">
                        {["Yes", "No"].map((m, index) => (
                          <div className="col-md-6" key={index}>
                            <label key={m} className="survey-choice">
                              <input
                                type="radio"
                                name="movement"
                                style={{ marginRight: "revert" }}
                                value={m}
                                onChange={handleChange}
                              />
                              {m}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="modal-footer border-0 pt-2">
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          resetSurveyForm();
                          setShowSurvey(false);
                        }}
                      >
                        Close
                      </button>

                      <button type="submit" className="btn btn-primary px-4">
                        Submit
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show" />
        </>
      )}

      {/* VISION / MISSION SECTION */}
      <section className="py-4 bg-light" id="Vision">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 col-xl-9">
              <p
                className="fs-5 fw-bold fst-italic text-start mb-2"
                style={{ color: "#1155cc" }}
              >
                Our Vision
              </p>

              <p className="fs-5 text-muted mb-4">
                A sustainable, resilient, and environmentally responsible Sierra
                Leone where development meets present needs without compromising
                future generations.
              </p>

              <p
                className="fs-5 fw-bold fst-italic text-start mb-2"
                style={{ color: "#1155cc" }}
              >
                Our Mission
              </p>

              <p className="fs-5 text-muted text-start mb-4">
                To support informed decision-making through inclusive
                environmental assessment, public participation, and scientific
                evidence.
              </p>

              <p
                className="fs-5 fw-bold fst-italic text-start mt-4 mb-2"
                style={{ color: "#1155cc" }}
              >
                FOCUS AREAS (PILLARS)
              </p>

              <p className="fs-5 text-muted">
                SEA’S 2050 is structured around five key pillars that guide the
                assessment, data collection, and long-term planning process.
                Each pillar addresses a critical area necessary for achieving
                sustainable development in Sierra Leone by 2050.
              </p>
            </div>
          </div>
          <div className="d-flex justify-content-center gap-3 flex-wrap">
            <button
              className="btn btn-primary btn-lg px-4"
              onClick={() => (window.location.href = "/focus-areas-pillars")}
            >
              Read More
            </button>
          </div>
        </div>
      </section>

      <section className="py-4" id="National">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 col-xl-9">
              <p
                className="fs-5 fw-bolder text-start mb-2"
                style={{ color: "#1155cc" }}
              >
                A National Vision Built by the People
              </p>

              <p className="fs-5 text-muted mb-4">
                SEA’s 2050 was established to provide Sierra Leone with a clear,
                shared, and sustainable development pathway toward 2050. The
                initiative is grounded in the belief that national development
                must be people-centred, forward-looking, and environmentally
                responsible.
              </p>
            </div>
          </div>
          <div className="d-flex justify-content-center gap-3 flex-wrap">
            <button
              className="btn btn-primary btn-lg px-4"
              onClick={() => (window.location.href = "/about")}
            >
              Read More
            </button>
          </div>
        </div>
      </section>

      <section className="py-4" id="survey">
        <div className="container">
          <div className="row justify-content-center">
            <img src={page1} />
            <div className="col-lg-10 col-xl-9">
              <p
                className="fs-5 fw-bolder text-center mb-2 mt-5"
                style={{ color: "#1155cc" }}
              >
                WHY SEA’s 2050 MATTERS
              </p>

              <p className="fs-5 fw-bold mb-3" style={{ color: "#1155cc" }}>
                Why This Survey?
              </p>
              <p className="fs-5 text-muted mb-3">
                The SEA’S 2050 Survey is a nationwide data-gathering effort
                aimed at capturing:
              </p>
              <ul className="fs-5 text-muted ps-3 mb-4">
                <li className="mb-2">
                  Public perceptions of environmental challenges
                </li>
                <li className="mb-2">Community priorities</li>
                <li className="mb-2">Sector-specific concerns</li>
                <li className="mb-2">Ideas for sustainable solutions</li>
              </ul>

              <p className="fs-5 fw-bold mb-3" style={{ color: "#1155cc" }}>
                The data collected will support:
              </p>
              <ul className="fs-5 text-muted ps-3 mb-5">
                <li className="mb-2">Policy development</li>
                <li className="mb-2">Environmental planning</li>
                <li className="mb-2">National and international reporting</li>
                <li className="mb-2">
                  Future investment and development strategies
                </li>
              </ul>
              <p className="fs-5 fw-bold mb-3" style={{ color: "#1155cc" }}>
                What the Survey Covers
              </p>

              <ul className="fs-5 text-muted ps-3">
                <li className="mb-2">Environment & climate change</li>
                <li className="mb-2">Natural resources</li>
                <li className="mb-2">Coastal and marine ecosystems</li>
                <li className="mb-2">Land use & urban development</li>
                <li className="mb-2">Energy & infrastructure</li>
                <li className="mb-2">Livelihoods & community wellbeing</li>
              </ul>
            </div>
          </div>
          <div className="d-flex justify-content-center gap-3 mt-3 flex-wrap">
            <button
              className="btn btn-primary btn-lg px-4"
              onClick={() =>
                (window.location.href = "/why-your-participation-matters")
              }
            >
              WHY YOUR PARTICIPATION MATTERS
            </button>

            <button
              className="btn btn-outline-primary btn-lg px-4"
              onClick={() => (window.location.href = "/who-should-participate")}
            >
              WHO SHOULD PARTICIPATE?
            </button>
          </div>
        </div>
      </section>

      <section className="py-4" id="planning">
        <div className="container">
          <div className="row justify-content-center">
            <img src={page1} />
            <div className="col-lg-10 col-xl-9">
              <p
                className="fs-5 fw-bolder text-center mb-2 mt-5"
                style={{ color: "#1155cc" }}
              >
                WHY SEA’s 2050 MATTERS
              </p>

              <p
                className="fs-5 fw-bold mb-3 mt-5"
                style={{ color: "#1155cc" }}
              >
                Planning Today for Tomorrow’s Generations
              </p>
              <p className="fs-5 text-muted mb-3">
                Decisions taken today will determine the quality of life for
                future generations. SEA’s 2050 provides a structured approach
                to:
              </p>
              <ul className="fs-5 text-muted ps-3 mb-4">
                <li className="mb-2">
                  Anticipate long-term economic, social, and environmental
                  challenges
                </li>
                <li className="mb-2">
                  Reduce risks and unintended consequences of development
                  decisions
                </li>
                <li className="mb-2">
                  Strengthen national resilience to climate change and global
                  shocks
                </li>
                <li className="mb-2">
                  Align national priorities with international sustainability
                  goals
                </li>
                <li className="mb-2">
                  This process ensures that development is not only ambitious,
                  but also sustainable and achievable.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-4" id="approach">
        <div className="container">
          <div className="row justify-content-center">
            <img
              src={earthImage}
              alt="Earth with hands"
              className="img-fluid earth-image mx-auto d-block mb-4"
            />

            <div className="col-lg-10 col-xl-9">
              <p
                className="fs-5 fw-bolder text-center mb-2 mt-5"
                style={{ color: "#1155cc" }}
              >
                OUR APPROACH
              </p>

              <p
                className="fs-5 fw-bold mb-3 mt-5"
                style={{ color: "#1155cc" }}
              >
                Strategic Environmental Assessment (SEA)
              </p>
              <p className="fs-5 text-muted mb-3">
                Strategic Environmental Assessment is a systematic,
                evidence-based process used worldwide to integrate
                sustainability considerations into policies, plans, and
                programmes. SEA’s 2050 applies this methodology through:
              </p>
              <ul className="fs-5 text-muted ps-3 mb-4">
                <li className="mb-2">Inclusive stakeholder engagement</li>
                <li className="mb-2">Baseline and situation analysis</li>
                <li className="mb-2">
                  Scenario development and alternatives assessment
                </li>
                <li className="mb-2">Public consultation and feedback</li>
                <li className="mb-2">
                  Data-driven analysis using modern technology
                </li>
                <li className="mb-2">
                  This approach strengthens transparency, accountability, and
                  long-term impact.
                </li>
              </ul>

              <p
                className="fs-5 fw-bolder text-center mb-5 mt-5"
                style={{ color: "#1155cc" }}
              >
                TECHNICAL FRAMEWORK
              </p>

              <ul className="fs-5 text-muted ps-3 mb-4">
                <li className="mb-2">
                  Stakeholder Engagement and Initial Inputs Capturing early
                  perspectives, priorities, and concerns from citizens and
                  institutions.
                </li>
                <li className="mb-2">
                  Baseline (Situation) Analysis Assessing current economic,
                  social, and environmental conditions across Sierra Leone.
                </li>
                <li className="mb-2">
                  Analysis of Alternatives (Scenario Planning) Exploring
                  different development pathways and their long-term
                  implications.
                </li>
                <li className="mb-2">
                  Choices for the Future Identifying preferred options based on
                  evidence, values, and sustainability goals.
                </li>
                <li className="mb-2">
                  Draft National Plan Consolidating findings into a strategic
                  framework with clear objectives and targets.
                </li>
                <li className="mb-2">
                  Stakeholder Review and Feedback Ensuring transparency,
                  ownership, and broad consensus.
                </li>
                <li className="mb-2">
                  Final Recommendations Providing strategic guidance for
                  policymakers and development partners.
                </li>
                <li className="mb-2">
                  Sector Programmes Developing detailed, costed programmes
                  across key national sector
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;
